import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { WebEntryLayout } from './entryHelpers';
import { patientAuthApi } from '../../api';
import { isDemoAuthEnabled } from '../../auth/patientAuthFlow';

/**
 * Password reset, end to end.
 *
 * The web app had no way back in for a patient who forgot their password —
 * the email login offered a "Send Reset Link" button that sent nothing and
 * said so in small print. Both halves exist on the server and are used here:
 * `POST /auth/forgot-password` sends a code to the account's own phone or
 * email, and `POST /auth/reset-password` sets the new password once that code
 * checks out. No session comes back: the reset signs every device out, so the
 * patient signs in again with the new password.
 */

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: T.textSec, fontFamily: T.font,
  marginBottom: 6, display: 'block',
};

export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<'phone' | 'email'>('phone');
  const [identifier, setIdentifier] = useState('');
  const [otpRequestId, setOtpRequestId] = useState<string | null>(null);
  const [demoOtp, setDemoOtp] = useState<string | undefined>();
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSend = async () => {
    setError('');
    if (!identifier.trim()) {
      setError(channel === 'phone' ? 'Enter your phone number.' : 'Enter your email address.');
      return;
    }
    setBusy(true);
    try {
      const r = await patientAuthApi.forgotPassword(channel, identifier);
      setOtpRequestId(r.otpRequestId);
      setDemoOtp(isDemoAuthEnabled() ? r.demoOtp : undefined);
    } catch (e: any) {
      // The server says plainly when no account matches; never soften it into
      // "if an account exists…" — the patient needs to know to register.
      setError(e?.body?.message || 'The code could not be sent. Check the number or address and try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setError('');
    if (code.trim().length < 4) { setError('Enter the code you received.'); return; }
    if (newPassword.length < 8) { setError('The new password must be at least 8 characters.'); return; }
    if (newPassword !== confirm) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      await patientAuthApi.resetPassword({
        otpRequestId: otpRequestId!,
        code,
        channel,
        identifier,
        newPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/patient/entry', { replace: true }), 2500);
    } catch (e: any) {
      setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'The password could not be reset. Request a new code and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <WebEntryLayout>
        <div style={{ padding: '48px 24px', textAlign: 'center', fontFamily: T.font }}>
          <div style={{
            width: 64, height: 64, borderRadius: 32, background: `${T.teal}1A`,
            display: 'grid', placeItems: 'center', margin: '0 auto 16px',
          }}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Password changed</div>
          <div style={{ fontSize: 12, color: T.textSec, marginTop: 8, lineHeight: 1.6, maxWidth: 300, margin: '8px auto 0' }}>
            Every device has been signed out. Sign in again with your new password.
          </div>
        </div>
      </WebEntryLayout>
    );
  }

  return (
    <WebEntryLayout>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '32px 24px', fontFamily: T.font,
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>Reset your password</div>
        <div style={{ fontSize: 12, color: T.textSec, marginTop: 6, textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
          {otpRequestId
            ? `Enter the code sent to your ${channel === 'phone' ? 'phone' : 'email'} and choose a new password.`
            : 'We will send a one-time code to the phone number or email on your account.'}
        </div>

        <div style={{ width: '100%', maxWidth: 340, marginTop: 22 }}>
          {!otpRequestId ? (
            <>
              {/* Channel choice */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {(['phone', 'email'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => { setChannel(c); setIdentifier(''); setError(''); }}
                    style={{
                      flex: 1, padding: '9px 0', borderRadius: 10, cursor: 'pointer',
                      fontSize: 12, fontWeight: 700, fontFamily: T.font,
                      color: channel === c ? T.white : T.textSec,
                      background: channel === c ? T.teal : T.white,
                      border: `1px solid ${channel === c ? T.teal : T.border}`,
                    }}
                  >
                    {c === 'phone' ? 'Phone' : 'Email'}
                  </button>
                ))}
              </div>

              <label style={labelStyle} htmlFor="reset-identifier">
                {channel === 'phone' ? 'Registered phone number' : 'Registered email address'}
              </label>
              <input
                id="reset-identifier"
                type={channel === 'phone' ? 'tel' : 'email'}
                placeholder={channel === 'phone' ? '98765 43210' : 'you@example.com'}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                style={inputStyle}
              />

              <button
                onClick={handleSend}
                disabled={busy}
                style={{
                  width: '100%', marginTop: 16, padding: '12px 0', border: 'none',
                  borderRadius: 12, cursor: busy ? 'default' : 'pointer',
                  fontSize: 14, fontWeight: 700, fontFamily: T.font, color: T.white,
                  background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </>
          ) : (
            <>
              {demoOtp && (
                <div style={{
                  fontSize: 10, color: T.warning, fontWeight: 700, marginBottom: 12,
                  padding: '6px 10px', background: `${T.warning}14`,
                  border: `1px solid ${T.warning}40`, borderRadius: 8, textAlign: 'center',
                }}>
                  DEV MODE — code is {demoOtp}
                </div>
              )}

              <label style={labelStyle} htmlFor="reset-code">Verification code</label>
              <input
                id="reset-code"
                inputMode="numeric"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={inputStyle}
              />

              <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="reset-new">New password</label>
              <input
                id="reset-new"
                type="password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
              />

              <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="reset-confirm">Confirm new password</label>
              <input
                id="reset-confirm"
                type="password"
                placeholder="Repeat the new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={inputStyle}
              />

              <button
                onClick={handleReset}
                disabled={busy}
                style={{
                  width: '100%', marginTop: 18, padding: '12px 0', border: 'none',
                  borderRadius: 12, cursor: busy ? 'default' : 'pointer',
                  fontSize: 14, fontWeight: 700, fontFamily: T.font, color: T.white,
                  background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Saving…' : 'Set new password'}
              </button>

              <button
                onClick={() => { setOtpRequestId(null); setCode(''); setError(''); }}
                style={{
                  width: '100%', marginTop: 10, padding: '10px 0', background: 'none',
                  border: `1px solid ${T.border}`, borderRadius: 10, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: T.textSec, fontFamily: T.font,
                }}
              >
                Use a different number or email
              </button>
            </>
          )}

          {error && (
            <div style={{ fontSize: 12, color: T.danger, marginTop: 14, textAlign: 'center', lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          <button
            onClick={() => navigate('/patient/entry')}
            style={{
              width: '100%', marginTop: 18, background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.teal, fontFamily: T.font,
            }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    </WebEntryLayout>
  );
}
